/**
 * Contract-formatting helpers — gedeeld tussen reducer (voor de
 * "bezoeker koos contract X"-customer-message in de assistent-flow)
 * en de inline contract-choice-renderer in `message-list.ts`.
 *
 * Bewust géén Lit-import: zo kan de reducer 'm gebruiken zonder
 * DOM-laagaanraking, en zo blijft de module unit-testbaar zonder
 * happy-dom op te tuigen.
 */

import type { ContractDetailed } from '../api/contracts';
import { i18n } from '../i18n/nl';

/**
 * Defensieve adres-formatting. Probeert drie veelvoorkomende Customers-
 * API-patronen in volgorde:
 *
 *   1. Gestructureerd: `street` + `houseNumber` (+ `houseNumberSuffix`)
 *      en optioneel `city`. Dutch-default voor `WoningnetUnit` etc.
 *   2. Pre-formatted: een `address`/`fullAddress`/`displayName`-string.
 *      Voor backends die het adres al server-side samenstellen.
 *   3. Fallback: "Eenheid {id-prefix}" — beter dan een lege card.
 */
export function formatUnitAddress(c: ContractDetailed): string {
  const unit = c.unit;
  if (!unit) return i18n.contracts.unitFallback(c.id);

  const street = pickString(unit, ['street', 'streetName']);
  const houseNumber = pickStringOrNumber(unit, ['houseNumber', 'number']);
  const suffix = pickString(unit, [
    'houseNumberSuffix',
    'houseNumberAddition',
    'addition',
  ]);
  const city = pickString(unit, ['city', 'cityName', 'place']);
  if (street && houseNumber) {
    const line1 = suffix
      ? `${street} ${houseNumber}${suffix.startsWith('-') ? suffix : ` ${suffix}`}`
      : `${street} ${houseNumber}`;
    return city ? `${line1}, ${city}` : line1;
  }

  const preformatted = pickString(unit, [
    'address',
    'fullAddress',
    'displayName',
    'label',
  ]);
  if (preformatted) return preformatted;

  return i18n.contracts.unitFallback(unit.id);
}

/** Periode-formatting: NL-locale, leesbaar i.p.v. ISO-strings. */
export function formatContractPeriod(c: ContractDetailed): string {
  const start = formatDate(c.startDate);
  const end = formatDate(c.endDate);
  if (!start) return '';
  return i18n.contracts.period(start, end);
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function pickString(
  obj: Record<string, unknown>,
  keys: string[],
): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return null;
}

function pickStringOrNumber(
  obj: Record<string, unknown>,
  keys: string[],
): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return null;
}
